<template>
  <div class="injection-new">
    <div style="margin: 32px 0 0 32px">
      <h2 class="inline-block" style="padding: 0; margin: 0 0 8px 0">
        <i class="submain">show_chart</i>
        {{ injectionName }}
      </h2>
      <div style="font-size: 13px">Setup new injection</div>
    </div>

    <div style="margin: 32px 32px 0">
      <template v-if="hasDevice">
        <DecoratorInput iconSize="sm" iconTitle="notes" style="margin-bottom: 16px">
          <textarea
            v-model="description"
            rows="1"
            placeholder="Description"
            class="injection-new__input"
          />
        </DecoratorInput>

        <TransitionExpand>
          <DecoratorInput
            v-if="fieldVial"
            iconSize="sm"
            iconTitle="group_work"
            style="margin-bottom: 16px"
          >
            <InputVials v-model="vial" :trayConfig="trayConfig" data-test-id="input-vial" />
          </DecoratorInput>
        </TransitionExpand>
      </template>

      <span style="padding: 16px 0 3px 0; display: inline-block; margin-bottom: 10px">
        Device and configuration
      </span>

      <div class="flex-responsive-2-columns">
        <DeviceMethodColumnInputs
          v-model="dmc"
          :isReadonly="readonlyDMC"
          :isRequired="true"
          :canSelectPostprocessing="hasDevice"
          :canPostprocessMultipleChannels="hasDevice"
          :isCalibrationSequenceMode="false"
          style="flex: 1 1 0"
        />
        <div v-if="hasDevice" class="button-container">
          <PopupHelper
            v-if="!isMethodCompatible"
            text="Selected method is not compatible with device"
          >
            <button
              class="button--square-image-primary"
              :disabled="!valid || isStarting"
              data-test-id="btn-start"
              @click="start"
            >
              <LoaderSpinner v-if="isStarting" class="injection-new__loader" />
              <template v-else>
                <i class="material-icons material-icon--18">play_arrow</i>
                Start
              </template>
            </button>
          </PopupHelper>
          <template v-else>
            <button
              class="button--square-image-primary"
              :disabled="!valid || isStarting"
              data-test-id="btn-start"
              @click="start"
            >
              <LoaderSpinner v-if="isStarting" class="injection-new__loader" />
              <template v-else>
                <i class="material-icons material-icon--18">play_arrow</i>
                Start
              </template>
            </button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import DeviceMethodColumnInputs from 'components/block/DeviceMethodColumnInputs.vue';
  import DecoratorInput from '@/uikitProject/decorators/DecoratorInput';

  import InputVials from '../../uikitProject/vials/vueInputVials/InputVials';
  import TransitionExpand from '@/uikitProject/transitions/TransitionExpand';
  import LoaderSpinner from '@/uikitProject/loaders/LoaderSpinner';
  import { getErrorMessage } from '@/utils/errorHelpers';
  import { convertNullValuesToUndefined } from '@/utils/objectHelpers';
  import {
    POSTPROCESSING_TEMPLATE_MAP,
    preparePostprocessingTemplateForRequest,
  } from '@/utils/postprocessingTemplateHelpers';
  import { apiInjections } from '@/api/graphql/cloud/injections';
  import { METHOD_RESET_REASONS } from '@/constants/hardware/eventReasons';
  import PopupHelper from '@/uikitProject/popups/info/PopupHelper.vue';
  import { DEFAULT_VIAL } from '@/constants/hardware/default';
  import { apiDevices } from '@/api/graphql/cloud/devices';

  const EVENT_INJECTION_CREATE = 'injectionCreate';

  export default {
    name: 'InjectionNewComponent',

    components: {
      PopupHelper,
      LoaderSpinner,
      TransitionExpand,
      InputVials,
      DeviceMethodColumnInputs,
      DecoratorInput,
    },

    props: {
      sampleSocket: Object,

      injectionName: { type: String, default: '1' },

      defaultVial: { type: [Number, String], default: '' },

      readonlyDMC: { type: Boolean, default: false }, // disable device, method, column changes
      defaultDevice: { type: Object, default: undefined }, // { id: ## }
      defaultMethod: { type: Object, default: undefined }, // { id: ## }
      defaultColumn: { type: Object, default: undefined }, // { id: ## }

      doResetOnDefaultValuesUpdate: {
        type: Boolean,
      },
      hasInjections: {
        type: Boolean,
      },

      sampleId: {
        type: Number,
        required: true,
      },
    },

    data() {
      return {
        description: '',
        vial: this.defaultVial,
        dmc: {
          device: this.defaultDevice,
          method: this.defaultMethod,
          column: this.defaultColumn,
          postprocessings: {
            D: null,
            D2: null,
            D3: null,
            D4: null,
          },
        },
        isInitialized: true,
        isStarting: false,
      };
    },

    computed: {
      hasDevice() {
        return this.dmc.device != null;
      },

      isMethodCompatible() {
        if (!this.hasDevice) {
          return false;
        }

        return this.dmc.device?.method?.id === this.dmc.method?.id
          ? this.dmc.device.method_invalidated_reason !==
              METHOD_RESET_REASONS.CONFIGURATION_INCOMPATIBLE
          : true;
      },

      valid() {
        return (
          this.hasDevice &&
          this.isMethodCompatible &&
          this.dmc.device != null &&
          this.dmc.method != null &&
          this.dmc.column != null &&
          (!this.fieldVial || this.vial)
        );
      },

      fieldVial() {
        if (!this.hasDevice) {
          return false;
        }

        return this.dmc.device?.configuration?.autosampler != null;
      },

      trayConfig() {
        if (!this.hasDevice) {
          return null;
        }

        const { autosampler } = this.dmc.device?.configuration;
        if (autosampler?.supported_trays && autosampler?.tray != null) {
          return Object.entries(autosampler?.supported_trays).find(
            ([, id]) => id === autosampler?.tray,
          )[0];
        }

        return null;
      },

      defaultValues() {
        return [this.defaultDevice, this.defaultMethod, this.defaultColumn];
      },

      postprocessingsForRequest() {
        const hasPostprocessings = Object.values(this.dmc.postprocessings).some(
          (postprocessing) => postprocessing != null,
        );

        if (!hasPostprocessings) {
          return null;
        }

        return Object.fromEntries(
          Object.entries(this.dmc.postprocessings).map(([channel, postprocessingTemplate]) => {
            if (postprocessingTemplate == null) {
              return [channel, undefined];
            }
            const {
              baseline_correction_anchor_point,
              algorithm_binj,
              blank_injection_measurement_ulid,
              detection_time_end_sec,
              detection_time_start_sec,
              algorithm_pdet,
              peak_detection_min_distance,
              peak_detection_min_height,
              peak_detection_min_prominence,
              peak_detection_min_width,
              algorithm_peak,
              peak_integration_quality_threshold,
              value_at_start,
              value_at_end,
              algorithm_nois,
            } = preparePostprocessingTemplateForRequest(postprocessingTemplate);

            const algorithm_base =
              POSTPROCESSING_TEMPLATE_MAP[postprocessingTemplate.algorithm_base];

            const postprocessing = {
              PDET: {
                algo: algorithm_pdet,
                params: convertNullValuesToUndefined({
                  peak_detection_min_distance,
                  peak_detection_min_height,
                  peak_detection_min_prominence,
                  peak_detection_min_width,
                }),
              },
              PEAK: {
                algo: algorithm_peak,
                params:
                  algorithm_peak === 'none'
                    ? {}
                    : algorithm_peak === 'first_derivative'
                    ? convertNullValuesToUndefined({
                        value_at_start,
                        value_at_end,
                      })
                    : convertNullValuesToUndefined({
                        peak_integration_quality_threshold,
                      }),
              },
              BASE: {
                algo: algorithm_base,
                params:
                  algorithm_base === 'manual'
                    ? {
                        baseline_correction_points:
                          postprocessingTemplate.baseline_correction_points,
                      }
                    : algorithm_base === 'horizontal_correction'
                    ? convertNullValuesToUndefined({
                        baseline_correction_anchor_point,
                      })
                    : {},
              },
              BINJ: {
                algo: algorithm_binj,
                params:
                  algorithm_binj === 'none'
                    ? {}
                    : convertNullValuesToUndefined({
                        blank_injection_measurement_ulid,
                      }),
              },
              NOIS: {
                algo: algorithm_nois,
                params: {},
              },
              DTIM: {
                algo: '',
                params: convertNullValuesToUndefined({
                  detection_time_start_sec,
                  detection_time_end_sec,
                }),
              },
            };

            return [channel, postprocessing];
          }),
        );
      },
    },

    watch: {
      defaultValues() {
        if (this.doResetOnDefaultValuesUpdate) {
          this.resetDMC();
        }
      },
    },

    methods: {
      resetDMC() {
        this.dmc = {
          device: this.defaultDevice,
          method: this.defaultMethod,
          column: this.defaultColumn,
        };
      },

      async start() {
        this.isStarting = true;
        try {
          await this.updateDeviceMethodColumn(
            this.dmc.device?.id,
            this.dmc.method?.id,
            this.dmc.column?.id,
          );
          const response = await apiInjections.startInjection(
            this.sampleId,
            this.dmc.device?.id,
            this.dmc.method?.id,
            this.dmc.column?.id,
            this.fieldVial ? this.vial : DEFAULT_VIAL,
            this.postprocessingsForRequest ?? {},
          );
          this.$emit(EVENT_INJECTION_CREATE, response.injection);
        } catch (e) {
          this.notifyError(getErrorMessage(e.message));
        } finally {
          this.isStarting = false;
        }
      },

      async updateDeviceMethodColumn(deviceID, methodID, columnID) {
        try {
          await apiDevices.updateDeviceMethodColumn(deviceID, methodID, columnID);
        } catch (e) {
          this.notifyError('Can not update device method or column!');
          throw e;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .injection-new {
    &__loader {
      display: inline-block;
    }

    &__warning {
      margin: 20px 32px 0;
      padding: 10px;
      background-color: $pure-color__yellow;
      text-align: center;
      border-radius: $border-radius__sm;
    }

    &__device-required {
      margin-bottom: 16px;
    }

    &__device-required-message {
      text-align: center;
      padding: 16px;
      background-color: #fff3cd;
      border: 1px solid #ffeaa7;
      border-radius: $border-radius__sm;
      color: #856404;
      font-weight: 500;
    }
  }

  .flex-responsive-2-columns {
    display: flex;
    align-items: stretch;

    flex-direction: column;
    @screen sm {
      flex-direction: row;
    }

    .button-container {
      display: flex;
      padding: 32px 0 0 32px;

      flex-direction: row-reverse;
      @screen sm {
        flex-direction: column-reverse;
      }
    }
  }
</style>
